import type { ClientToServerEvents, ServerToClientEvents } from '@rfleet/shared';
import { WS_NAMESPACE } from '@rfleet/shared';
import { useEffect } from 'react';
import { io, type Socket } from 'socket.io-client';
import { refreshSession, tokenStore } from './api';

type LiveSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: LiveSocket | null = null;
let currentToken: string | null = null;

const WS_URL = (import.meta.env['VITE_WS_URL'] as string | undefined) ?? '';

/** Shared authenticated socket (created lazily, reconnects with a fresh access token). */
export function getSocket(): LiveSocket {
  if (socket) return socket;
  socket = io(`${WS_URL}${WS_NAMESPACE}`, {
    autoConnect: false,
    transports: ['websocket'],
    auth: (cb) => cb({ token: currentToken }),
    reconnectionDelayMax: 10_000,
  });
  socket.on('connect_error', async () => {
    // Token may have expired: refresh, then socket.io retries with the new auth callback
    if (await refreshSession()) currentToken = tokenStore.peekAccess();
  });
  return socket;
}

export function connectSocket(): void {
  currentToken = tokenStore.peekAccess();
  const s = getSocket();
  if (!s.connected) s.connect();
}

export function disconnectSocket(): void {
  socket?.disconnect();
}

/** Subscribes to an event for the component lifetime. */
export function useSocketEvent<E extends keyof ServerToClientEvents>(event: E, handler: ServerToClientEvents[E]): void {
  useEffect(() => {
    const s = getSocket();
    s.on(event, handler as never);
    return () => {
      s.off(event, handler as never);
    };
  }, [event, handler]);
}
